import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { ErrorState } from '../ui/error-state'
import { Input } from '../ui/input'
import { LoadingTable } from '../ui/loading-states'
import { Pagination } from '../ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { usePharmaMovements } from '../../hooks/usePharmaAnalytics'
import {
  formatCurrency,
  formatIsoDate,
  formatNumber,
} from '../../utils/dateFormat'
import type { PharmaMovementsFilters } from '../../types/pharma-analytics.types'

interface MovementsTabProps {
  filters: PharmaMovementsFilters
  onFiltersChange: (
    update: (prev: PharmaMovementsFilters) => PharmaMovementsFilters
  ) => void
  operationTypes: string[]
}

const OPERATION_LABELS: Record<string, string> = {
  purchase: 'Закупка',
  writeoff: 'Списание',
  transfer_in: 'Перемещение (приход)',
  transfer_out: 'Перемещение (расход)',
  surplus: 'Излишки',
  return: 'Возврат',
}

export function MovementsTab({
  filters,
  onFiltersChange,
  operationTypes,
}: MovementsTabProps) {
  const { data, isLoading, error, refetch } = usePharmaMovements(filters)

  const rows = data?.rows ?? []
  const limit = filters.limit ?? 50
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / limit))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Движения регистра</CardTitle>
        <CardDescription>
          Построчная расшифровка под витриной оборота: что пришло и что ушло со
          склада. Выдача медикаментов пациентам («Входящее списание») сюда не входит —
          она регистр не двигает.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-muted-foreground">С даты</label>
            <Input
              type="date"
              className="w-40"
              value={filters.from ?? data?.period.from ?? ''}
              onChange={(e) =>
                onFiltersChange((prev) => ({
                  ...prev,
                  from: e.target.value || undefined,
                  page: 1,
                }))
              }
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">По дату</label>
            <Input
              type="date"
              className="w-40"
              value={filters.to ?? data?.period.to ?? ''}
              onChange={(e) =>
                onFiltersChange((prev) => ({
                  ...prev,
                  to: e.target.value || undefined,
                  page: 1,
                }))
              }
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">Препарат</label>
            <Input
              placeholder="Наименование или код, Enter"
              defaultValue={filters.medication ?? ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onFiltersChange((prev) => ({
                    ...prev,
                    medication: (e.target as HTMLInputElement).value || undefined,
                    page: 1,
                  }))
                }
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <Button
            size="sm"
            variant={filters.operationType ? 'outline' : 'default'}
            onClick={() =>
              onFiltersChange((prev) => ({
                ...prev,
                operationType: undefined,
                page: 1,
              }))
            }
          >
            Все операции
          </Button>
          {operationTypes.map((type) => (
            <Button
              key={type}
              size="sm"
              variant={filters.operationType === type ? 'default' : 'outline'}
              onClick={() =>
                onFiltersChange((prev) => ({
                  ...prev,
                  operationType: type,
                  page: 1,
                }))
              }
            >
              {OPERATION_LABELS[type] ?? type}
            </Button>
          ))}
          <div className="ml-auto text-sm text-muted-foreground tabular-nums">
            Строк: {formatNumber(data?.total ?? 0)}
          </div>
        </div>

        {isLoading ? (
          <LoadingTable rows={8} columns={6} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить движения"
            description={(error as Error)?.message}
            onAction={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Движений за период не найдено
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Дата</TableHead>
                    <TableHead>Операция</TableHead>
                    <TableHead>Препарат</TableHead>
                    <TableHead>Склад</TableHead>
                    <TableHead className="text-right">Количество</TableHead>
                    <TableHead className="text-right">Сумма</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={`${row.movementDate}-${row.warehouseCode}-${row.medicationCode}-${row.operationType}-${index}`}
                    >
                      <TableCell className="whitespace-nowrap">
                        {formatIsoDate(row.movementDate)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={row.direction === 'in' ? 'default' : 'secondary'}
                        >
                          {OPERATION_LABELS[row.operationType] ?? row.operationType}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate" title={row.medicationName ?? ''}>
                          {row.medicationName ?? row.medicationCode}
                        </div>
                        {row.batchNumber && (
                          <div className="text-xs text-muted-foreground">
                            партия {row.batchNumber}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <div className="truncate" title={row.warehouseName ?? ''}>
                          {row.warehouseName ?? '—'}
                        </div>
                        {row.financeSource && (
                          <div className="text-xs text-muted-foreground">
                            {row.financeSource}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        {formatNumber(row.quantity, { maximumFractionDigits: 3 })}
                        {row.unit && (
                          <span className="text-xs text-muted-foreground ml-1">
                            {row.unit}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {formatCurrency(row.costPurchase)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={filters.page ?? 1}
              totalPages={totalPages}
              onPageChange={(page) =>
                onFiltersChange((prev) => ({ ...prev, page }))
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
